import React, { useEffect, useState } from 'react';
import {
  LinkIcon,
  DocumentMagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import Modal from '../../../../components/ui/Modal';
import {
  FormActions,
  FormError,
  FormField,
  FormIntro,
  FormOption,
  FormOptionList,
  GenerateLabel,
} from '../../../../components/ui/FormModalParts';
import { splitTrimmed } from '../../../../utils/text';
import { reportSettingsOf } from '../utils/reportSettings';

const SessionReportGenerateModal = ({
  isOpen,
  onClose,
  onSubmit,
  sessionTitle = '',
  isRegenerating = false,
  initialSettings = null,
}) => {
  const [deeplinksEnabled, setDeeplinksEnabled] = useState(true);
  const [inDepthEnabled, setInDepthEnabled] = useState(true);
  const [verifiedReport, setVerifiedReport] = useState(false);
  const [generateQa, setGenerateQa] = useState(false);
  const [questions, setQuestions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const settings = reportSettingsOf(initialSettings);
    setDeeplinksEnabled(settings.deeplinks_enabled);
    setInDepthEnabled(settings.in_depth_enabled);
    setVerifiedReport(settings.report_pipeline === 'v3');
    setGenerateQa(settings.generate_qa);
    setQuestions(settings.questions.join('\n'));
    setLoading(false);
    setError('');
  }, [isOpen, initialSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const questionsList = splitTrimmed(questions);
    if (generateQa && questionsList.length === 0) {
      setError('Enter at least one question when Q&A analysis is enabled.');
      return;
    }

    const saved = reportSettingsOf(initialSettings);
    setLoading(true);
    try {
      await onSubmit({
        ...saved,
        deeplinks_enabled: deeplinksEnabled,
        in_depth_enabled: inDepthEnabled,
        report_pipeline: verifiedReport ? 'v3' : 'v1',
        generate_qa: generateQa && questionsList.length > 0,
        questions: questionsList,
      });
    } catch (err) {
      setError(err.message || 'Failed to start report generation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isRegenerating ? 'Regenerate Session Report' : 'Generate Session Report'}
      size="lg"
      className="event-modal"
    >
      <form onSubmit={handleSubmit} className="event-modal-form">
        <FormIntro title={sessionTitle || 'Session report'}>
          Choose generation options before starting the report pipeline.
        </FormIntro>

        <FormOptionList title="Generation options">
          <FormOption
            checked={deeplinksEnabled}
            onToggle={() => setDeeplinksEnabled((prev) => !prev)}
            disabled={loading}
            Icon={LinkIcon}
            title="Include deeplinks"
          >
            When publishing to DigWatch or FAICON, convert transcript citations into
            clickable links back to the session page.
          </FormOption>
          <FormOption
            checked={inDepthEnabled}
            onToggle={() => setInDepthEnabled((prev) => !prev)}
            disabled={loading}
            Icon={DocumentMagnifyingGlassIcon}
            title="Include in-depth analysis"
          >
            Generate the additional analysis section: agreements, differences, takeaways,
            thought-provoking comments, and follow-up questions. Uncheck to skip it.
          </FormOption>
          <FormOption
            checked={verifiedReport}
            onToggle={() => setVerifiedReport((prev) => !prev)}
            disabled={loading}
            Icon={ShieldCheckIcon}
            title="Verified full report (v3)"
          >
            Write the full session report speaker by speaker and check every sentence against
            the transcript sentences it cites. More accurate attribution, takes longer.
            English sessions with the default model only; otherwise the standard report is used.
          </FormOption>
          <FormOption
            checked={generateQa}
            onToggle={() => setGenerateQa((prev) => !prev)}
            disabled={loading}
            Icon={QuestionMarkCircleIcon}
            title="Q&A analysis"
          >
            Analyze custom questions against this session transcript. Shown as an extra tab
            in the app; included when you publish to DigWatch (not sent to FAICON).
          </FormOption>
        </FormOptionList>

        {generateQa && (
          <FormField
            id="session-qa-questions"
            label="Questions for analysis"
            hint="Enter one question per line. The AI will search the session transcript for relevant discussion."
          >
            <textarea
              id="session-qa-questions"
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              rows={5}
              className="event-modal-control"
              placeholder={'Enter one question per line, e.g.:\nWhat were the main conclusions?\nWere there any disagreements?'}
              disabled={loading}
            />
          </FormField>
        )}

        <FormError message={error} />

        <FormActions onCancel={handleClose} disabled={loading}>
          <GenerateLabel starting={loading} label={isRegenerating ? 'Regenerate report' : 'Generate report'} />
        </FormActions>
      </form>
    </Modal>
  );
};

export default SessionReportGenerateModal;
